import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import {
  BusinessStatus,
  ProspectFiltering,
} from '@/experiences/sales-portal/domain/prospects/blocs/hooks/use-prospect-finder-filtering';
import { createStyles } from '@/contexts/theme-context';
import {
  FilterDropdownMenu,
  FilterDropdownOption,
} from '@/experiences/sales-portal/components/filter-dropdown-menu';
import { HorizontalScrollWithArrows } from '@/experiences/sales-portal/components/horizontal-scroll-with-arrows';
import { PillButton, PillButtonType } from '@usepepper/ui/atoms/pill-button';
import { SalesPortalAnalyticEvent } from '@/experiences/sales-portal/analytics/fennel/registry';
import { SortOrder } from '@/experiences/sales-portal/domain/prospects/blocs/loaders/use-prospect-finder-nearby-businesses-loader';
import { track } from '@/experiences/sales-portal/analytics/fennel/hook';

const PRICE_OPTIONS = ['1', '2', '3', '4'] as const;
const PRICE_LABEL_KEYS: Record<string, string> = {
  '1': 'sales_portal.prospect_finder.filter.price.budget',
  '2': 'sales_portal.prospect_finder.filter.price.mid_range',
  '3': 'sales_portal.prospect_finder.filter.price.upscale',
  '4': 'sales_portal.prospect_finder.filter.price.fine_dining',
};
const SORT_OPTIONS: SortOrder[] = [
  SortOrder.BEST_MATCH,
  SortOrder.DISTANCE,
  SortOrder.RATING,
];
// UI labels diverge from backend enum values: DISTANCE → "Nearest First",
// RATING → "Top Rated". Backend still receives best_match / distance / rating
// (the Yelp API's accepted sort_by values).
const SORT_LABEL_KEYS: Record<SortOrder, string> = {
  [SortOrder.BEST_MATCH]: 'sales_portal.prospect_finder.filter.sort.best_match',
  [SortOrder.DISTANCE]: 'sales_portal.prospect_finder.filter.sort.distance',
  [SortOrder.RATING]: 'sales_portal.prospect_finder.filter.sort.rating',
};

interface FilterChipRowProps {
  filtering: ProspectFiltering;
  onFilterChange: () => void;
}

export const FilterChipRow: React.FC<FilterChipRowProps> = ({
  filtering,
  onFilterChange,
}) => {
  const [styles] = useStyles();
  const [openDropdown, setOpenDropdown] = useState<
    'category' | 'sort' | 'price' | null
  >(null);

  const toggleStatus = (status: BusinessStatus) => {
    const next = filtering.businessStatus === status ? null : status;
    track(SalesPortalAnalyticEvent.PROSPECT_FINDER_STATUS_FILTER_CLICK, {
      status: next,
    });
    filtering.setBusinessStatus(next);
    onFilterChange();
  };

  const applyCategories = (keys: string[]) => {
    const next = new Set(keys);
    new Set([...filtering.selectedCategories, ...next]).forEach((key) => {
      const selected = next.has(key);
      if (selected !== filtering.selectedCategories.has(key)) {
        track(SalesPortalAnalyticEvent.PROSPECT_FINDER_CATEGORY_FILTER_CLICK, {
          category: key,
          selected,
        });
      }
    });
    filtering.setSelectedCategories(next);
    onFilterChange();
  };

  const toggleCategory = (key: string) => {
    const next = new Set(filtering.selectedCategories);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    applyCategories([...next]);
  };

  const applyPrices = (keys: string[]) => {
    const next = new Set(keys);
    new Set([...filtering.selectedPriceRanges, ...next]).forEach((key) => {
      const selected = next.has(key);
      if (selected !== filtering.selectedPriceRanges.has(key)) {
        track(SalesPortalAnalyticEvent.PROSPECT_FINDER_PRICE_FILTER_CLICK, {
          price: key,
          selected,
        });
      }
    });
    filtering.setSelectedPriceRanges(next);
    onFilterChange();
  };

  const togglePrice = (key: string) => {
    const next = new Set(filtering.selectedPriceRanges);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    applyPrices([...next]);
  };

  const currentPill: PillButtonType = {
    active: filtering.businessStatus === BusinessStatus.CURRENT,
    label: i18n.t('sales_portal.prospect_finder.filter.status.existing'),
    onPress: () => toggleStatus(BusinessStatus.CURRENT),
  };

  const prospectPill: PillButtonType = {
    active: filtering.businessStatus === BusinessStatus.PROSPECT,
    label: i18n.t('sales_portal.prospect_finder.filter.status.prospect'),
    onPress: () => toggleStatus(BusinessStatus.PROSPECT),
  };

  const categoryPill: PillButtonType = {
    active: filtering.selectedCategories.size > 0,
    count: filtering.selectedCategories.size || undefined,
    countPosition: 'after',
    dropDown: true,
    label: i18n.t('sales_portal.prospect_finder.filter.category'),
    onPress: () =>
      setOpenDropdown(openDropdown === 'category' ? null : 'category'),
  };

  const sortPill: PillButtonType = {
    active: filtering.sortOrder !== SortOrder.BEST_MATCH,
    dropDown: true,
    label: i18n.t(SORT_LABEL_KEYS[filtering.sortOrder]),
    onPress: () => setOpenDropdown(openDropdown === 'sort' ? null : 'sort'),
  };

  const pricePill: PillButtonType = {
    active: filtering.selectedPriceRanges.size > 0,
    count: filtering.selectedPriceRanges.size || undefined,
    countPosition: 'after',
    dropDown: true,
    label: i18n.t('sales_portal.prospect_finder.filter.price'),
    onPress: () => setOpenDropdown(openDropdown === 'price' ? null : 'price'),
  };

  const categoryOptions: FilterDropdownOption<string>[] =
    filtering.categoryOptions.map((option) => ({
      key: option.value,
      label: option.label,
      selected: filtering.selectedCategories.has(option.value),
    }));

  const sortOptions: FilterDropdownOption<SortOrder>[] = SORT_OPTIONS.map(
    (option) => ({
      key: option,
      label: i18n.t(SORT_LABEL_KEYS[option]),
      selected: filtering.sortOrder === option,
    }),
  );

  const priceOptions: FilterDropdownOption<string>[] = PRICE_OPTIONS.map(
    (value) => ({
      key: value,
      label: i18n.t(PRICE_LABEL_KEYS[value]),
      selected: filtering.selectedPriceRanges.has(value),
    }),
  );

  return (
    <HorizontalScrollWithArrows
      backAccessibilityLabel={i18n.t(
        'sales_portal.prospect_finder.filter.scroll_left',
      )}
      contentContainerStyle={styles.rowContent}
      forwardAccessibilityLabel={i18n.t(
        'sales_portal.prospect_finder.filter.scroll_right',
      )}
      style={styles.row}
    >
      <PillButton button={currentPill} variant="small" />
      <PillButton button={prospectPill} variant="small" />

      <FilterDropdownMenu
        isOpen={openDropdown === 'category'}
        onApply={applyCategories}
        onOpenChange={(open) => setOpenDropdown(open ? 'category' : null)}
        onToggle={toggleCategory}
        options={categoryOptions}
        trigger={<PillButton button={categoryPill} variant="small" />}
      />

      <FilterDropdownMenu
        isOpen={openDropdown === 'sort'}
        mode="single"
        onOpenChange={(open) => setOpenDropdown(open ? 'sort' : null)}
        onToggle={(key) => {
          setOpenDropdown(null);
          if (key === filtering.sortOrder) {
            return;
          }
          track(SalesPortalAnalyticEvent.PROSPECT_FINDER_SORT_CLICK, {
            sortBy: key,
          });
          filtering.setSortOrder(key);
          onFilterChange();
        }}
        options={sortOptions}
        trigger={<PillButton button={sortPill} variant="small" />}
      />

      <FilterDropdownMenu
        isOpen={openDropdown === 'price'}
        onApply={applyPrices}
        onOpenChange={(open) => setOpenDropdown(open ? 'price' : null)}
        onToggle={togglePrice}
        options={priceOptions}
        trigger={<PillButton button={pricePill} variant="small" />}
      />
    </HorizontalScrollWithArrows>
  );
};

const useStyles = createStyles((theme) => ({
  // Horizontal padding matches the search input so the arrows line up with it.
  row: {
    flexGrow: 0,
    flexShrink: 0,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  rowContent: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceXsmall,
  },
}));
